/**
 * 業務フローの図の見出し — ステージへの導線・型の札・名前・エラー（型の整合の注意・違反の件数）。
 *
 * マスターの図（`StoryFlowView`）と案件から見た図（`PjStoryFlowView`）が同じ見出しを出す。
 * 画面ごとに書くと、同じ業務フローの入口の文が画面によって違う形で出る。
 *
 * **見出しに出すのは導線・名前・エラーだけにする**（Dhik 2026-09-29「テキスト量が多いことが
 * 課題」）。型（メイン ／ サブ）は名前の `title` に、状況・入口・出口は名前の横の ⓘ に束ねる。入口と
 * 出口は図の開始の節点と出口の札が持っており、図の上に重ねて書くと図を読む前に読む行が増える。
 * 違反の件数は 1 件以上のときだけ出す（直すべき誤り）。更新日時は出さない。
 *
 * **型の札（業務フロー）は名前の左に出す。** どのアウトプットの詳細かを見失わないためである
 * （Dhik 2026-09-29「流石にないと迷子になる」）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フローの見出しを、ステージへの導線・型の札・名前（型は指すと出る）・状況と入口と出口の ⓘ・型の整合の注意・1 件以上のときの違反の件数で描く。
 * @graph-connects NodeTypeBadge [calls] 名前の左の型の札
 * @graph-connects FlowInfoMark [calls] 状況・入口・出口の ⓘ
 * @graph-connects flowEntryText [calls] 入口の文
 * @graph-connects flowExitText [calls] 出口の文
 */
import type { FlowView } from '@cortex/domain-spec-console';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { useTranslation } from 'react-i18next';

import { NodeTypeBadge } from '../../components/spec/NodeTypeBadge.js';
import { StageLink } from '../view-links/flow.js';
import { FlowInfoMark } from './flow-info-mark.js';
import {
  endingRowText,
  flowEntryText,
  flowExitText,
  flowTypeText,
  refLabelOf,
} from './flow-text.js';

/** `FlowHeading` の props。 @graph-connects none */
export interface FlowHeadingProps {
  /** 見出しに出す業務フロー */
  flow: FlowView;
  /** 重ねる案件番号。ステージへの導線に引き継ぐ。master なら undefined */
  pjNumber: number | undefined;
  /** 見出しの名前。省けば業務フローの名前（案件で名前がまだ空のとき、変更前の名前を渡す） */
  label?: string;
}

/**
 * 状況・入口・出口の文の行。ⓘ に束ねる。
 *
 * @param flow 業務フロー
 * @param t 訳
 * @returns ⓘ に出す行
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フローの状況・入口・出口（出口に着く結末つき）を、ⓘ に束ねる文の行にする。
 * @graph-connects flowEntryText [calls] 入口の文
 * @graph-connects flowExitText [calls] 出口の文
 */
export function flowHeadingInfoLines(
  flow: FlowView,
  t: (key: string, options?: Record<string, unknown>) => string,
): string[] {
  const exitEndings =
    flow.exit.endings.length === 0
      ? ''
      : ` ${t('flow.exitEndings', {
          rows: flow.exit.endings.map((row) => endingRowText(row, t)).join(t('flow.listSeparator')),
        })}`;
  return [
    ...(flow.situation === null
      ? []
      : [`${t('flow.situation')}: ${refLabelOf(flow.situation, t)}`]),
    `${t('flow.entryTitle')}: ${flowEntryText(flow.entry, t)}`,
    `${t('flow.exitTitle')}: ${flowExitText(flow.exit, t)}${exitEndings}`,
  ];
}

/**
 * 業務フローの見出し。
 *
 * **型の整合が崩れていれば注意の札を出す**（制約 63）。型か置き場が間違っていることを、図を読む
 * 前に知らせる。理由の箇条は直す人だけが読むので、札を押して開く畳みに入れる。
 *
 * @param props 業務フロー・案件番号・見出しの名前
 * @returns 見出し
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フローの見出しを、ステージへの導線・型の札・名前・ⓘ・畳んだ型の整合の注意・1 件以上のときの違反の件数で描く。
 * @graph-connects NodeTypeBadge [calls] 名前の左の型の札
 * @graph-connects StageLink [calls] ステージの図への導線
 * @graph-connects FlowInfoMark [calls] 状況・入口・出口の ⓘ
 * @graph-connects flowHeadingInfoLines [calls] ⓘ に束ねる状況・入口・出口の行
 * @graph-connects flowTypeText [calls] 名前を指すと出す型の語
 */
export function FlowHeading({ flow, pjNumber, label }: FlowHeadingProps) {
  const { t } = useTranslation();
  const errorCount = flow.violations.filter((violation) => violation.severity === 'error').length;
  const name = label ?? flow.label;

  return (
    <header className="flex flex-col gap-2">
      {flow.stage !== null && (
        <div className="text-xs text-[var(--color-text-muted)]">
          <span data-flow-stage={flow.stage.id}>
            <StageLink stageId={flow.stage.id} pjNumber={pjNumber}>
              {refLabelOf(flow.stage, t)}
            </StageLink>
            {' ›'}
          </span>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <NodeTypeBadge nodeType="Flow" />
        <h1
          className="text-2xl font-bold text-[var(--color-text-primary)]"
          data-flow-heading={flow.flowId}
          data-flow-type={flow.type}
          title={flowTypeText(flow.type, t)}
        >
          {name === '' ? t('storyFlow.labelMissing') : name}
        </h1>
        <FlowInfoMark lines={flowHeadingInfoLines(flow, t)} />
      </div>
      {!flow.roleCheck.consistent && (
        <details
          role="note"
          data-flow-role-check="inconsistent"
          className="rounded border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-3 py-2 text-sm"
        >
          <summary className="cursor-pointer">
            <Badge variant="warning">
              {t('flow.roleCheckTitle', { type: flowTypeText(flow.roleCheck.declared, t) })}
            </Badge>
          </summary>
          <ul className="mt-1 list-disc pl-5 text-xs text-[var(--color-text-secondary)]">
            {flow.roleCheck.reasons.map((reason) => (
              <li key={reason}>{reason}</li>
            ))}
          </ul>
        </details>
      )}
      {flow.violations.length > 0 && (
        <span
          className="text-xs text-[var(--color-text-muted)]"
          data-flow-violations={flow.violations.length}
        >
          {t('flow.violations', { count: flow.violations.length, errors: errorCount })}
        </span>
      )}
    </header>
  );
}
